import {
  createContext, useState, useEffect, useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { useMutation } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import httpClient from '../../datasources/httpClient';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [registerError, setRegisterError] = useState(null);
  const [authenticationError, setAuthenticationError] = useState(null);
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenthicated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await httpClient.get('users?current=true');
        setUser(response.data);
        setIsAuthenthicated(true);
      } catch (error) {
        localStorage.clear();
        navigate('/');
      } finally {
        setIsLoading(false);
      }
    };

    const token = localStorage.getItem('token');
    if (token) {
      httpClient.defaults.headers.Authorization = `Bearer ${token}`;
      fetchUser();
    } else {
      setIsLoading(false);
    }
  }, []);

  function handleTokenFromResponse(response) {
    setRegisterError(null);
    setAuthenticationError(null);

    const { data: { token, refreshToken, user: newUser } } = response;

    localStorage.setItem('user', JSON.stringify(newUser));
    localStorage.setItem('token', token);
    localStorage.setItem('refreshToken', refreshToken);
    httpClient.defaults.headers.Authorization = `Bearer ${token}`;
    setIsAuthenthicated(true);
    setIsLoading(false);
    setUser(newUser);
    if (location.state?.from) {
      navigate(location.state.from);
    } else {
      navigate('/home');
    }
  }

  const mutation = useMutation(
    ({ email, password }) => httpClient.post(
      'auth/sign-in',
      { email, password },
    ),
    {
      onSuccess: (response) => handleTokenFromResponse(response),
      onError: (error) => {
        setIsLoading(false);
        const { response: { data: { message }, status } } = error;

        if (status === 400) {
          setAuthenticationError(message);
        }
      },
    },
  );

  const registerMutation = useMutation(
    ({
      name, email, password, confirmPassword,
    }) => httpClient.post('auth/sign-up', {
      name, email, password, confirmPassword,
    }),
    {
      onSuccess: (response) => handleTokenFromResponse(response),
      onError: (error) => {
        setIsLoading(false);
        const { response: { data: { error: message } } } = error;

        setRegisterError(message);
      },
    },
  );

  const forgotPasswordMutation = useMutation(
    ({
      email,
    }) => httpClient.post('auth/forgot-password', {
      email,
    }),
    {
      onSuccess: () => navigate('/'),
      onError: (error) => {
        const { response: { data: { error: message } } } = error;

        setRegisterError(message);
      },
    },
  );

  const resetPasswordMutation = useMutation(
    ({
      password, confirmPassword, id, token,
    }) => httpClient.post(
      `auth/reset-password/${id}/${token}`,
      { confirmPassword, password },
    ),
    {
      onSuccess: () => {
        navigate('/');
      },
      onError: (error) => {
        const { response: { data: { error: message }, status } } = error;

        if (status === 400) {
          setAuthenticationError(message);
        }
      },
    },
  );

  async function handleResetPassword({
    id, token, password, confirmPassword,
  }) {
    resetPasswordMutation.mutate({
      id, token, password, confirmPassword,
    });
  }

  async function handleForgotPassword({
    email,
  }) {
    forgotPasswordMutation.mutate({
      email,
    });
  }

  async function handleRegister({
    name, email, password, confirmPassword,
  }) {
    registerMutation.mutate({
      name, email, password, confirmPassword,
    });
  }

  async function handleAuthenticate({ email, password }) {
    mutation.mutate({ email, password });
  }

  function handleLogout() {
    navigate('/');

    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    httpClient.defaults.headers.Authorization = null;
    setIsAuthenthicated(false);
  }

  const value = useMemo(() => ({
    authenticationError,
    registerError,
    user,
    isAuthenticated,
    handleAuthenticate,
    handleLogout,
    handleRegister,
    handleResetPassword,
    handleForgotPassword,
    isLoading: mutation.isLoading || registerMutation.isLoading
      || resetPasswordMutation.isLoading || forgotPasswordMutation.isLoading || isLoading,
  }), [registerError, authenticationError, isAuthenticated,
    isLoading, mutation, registerMutation, resetPasswordMutation, forgotPasswordMutation]);

  return (
    <AuthContext.Provider
      value={value}
    >
      {children}
    </AuthContext.Provider>
  );
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default AuthContext;
